import * as React from 'react';
import { Check } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  optionCardIndicatorRecipe,
  optionCardRecipe,
  type OptionCardRecipeProps,
} from '../recipes/option-card.recipe';
import { uiMarkerInternal } from '../recipes/shared';
import { onInnerControl } from './inner-control';
import { Icon } from './Icon';
import { Text } from './Text';

export interface OptionCardProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'title' | 'onSelect'>,
    Omit<OptionCardRecipeProps, 'selected'> {
  selected?: boolean;
  disabled?: boolean;
  /**
   * The mark that says "chosen". `radio` for one-of (company size, a plan),
   * `check` for any-of (photos to include). `none` when the border is enough —
   * the destination pickers put a whole selector inside the card instead.
   */
  indicator?: 'radio' | 'check' | 'none';
  /** A lucide icon beside the title. */
  icon?: LucideIcon;
  title?: React.ReactNode;
  description?: React.ReactNode;
  /**
   * Render as a div rather than a button.
   *
   * A button cannot contain another control, and two of the call sites put a
   * Select inside the selected card. As a div it keeps the role, the tab stop
   * and the Enter/Space handling, so it is still a button to the keyboard.
   */
  as?: 'button' | 'div';
  onSelect?: () => void;
}

/**
 * One of several answers, drawn as a card.
 *
 *   <OptionCard
 *     selected={type === 'location'}
 *     onSelect={() => setType('location')}
 *     icon={MapPin}
 *     title="Storage location"
 *     description="Tow to one of your lots"
 *   />
 *
 * In a group, pass `role="radio"` on each and put `role="radiogroup"` on the
 * parent; the card then announces as checked rather than pressed.
 */
export const OptionCard = React.forwardRef<HTMLElement, OptionCardProps>(
  function OptionCard(
    {
      selected = false,
      disabled = false,
      indicator = 'none',
      icon,
      title,
      description,
      padding,
      radius,
      as = 'button',
      onSelect,
      onClick,
      onKeyDown,
      role,
      className,
      children,
      ...props
    },
    ref
  ) {
    const isRadio = role === 'radio' || role === 'checkbox';
    const stateProps = isRadio
      ? { 'aria-checked': selected }
      : { 'aria-pressed': selected };

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      onClick?.(event);
      if (event.defaultPrevented || disabled) return;
      // A click on a control inside the card is for that control. The
      // destination pickers used to wrap theirs in a div that stopped
      // propagation to get this; here it is the default.
      if (onInnerControl(event.currentTarget, event.target)) return;
      onSelect?.();
    };

    const content = (
      <>
        {title || icon || indicator !== 'none' ? (
          <div className={`${uiMarkerInternal} flex w-full items-center gap-2`}>
            {icon ? <Icon icon={icon} size="md" tone="primary" /> : null}
            {title ? (
              <Text variant="body-strong" as="span" className="min-w-0 flex-1">
                {title}
              </Text>
            ) : (
              <span className={`${uiMarkerInternal} flex-1`} />
            )}
            {indicator !== 'none' ? (
              <span
                aria-hidden="true"
                className={optionCardIndicatorRecipe({ indicator, selected })}
              >
                {indicator === 'radio' ? (
                  <span
                    className={cn(
                      uiMarkerInternal,
                      'size-2 rounded-full bg-primary-foreground',
                      !selected && 'invisible'
                    )}
                  />
                ) : (
                  <Check className="size-icon-xs" strokeWidth={3} />
                )}
              </span>
            ) : null}
          </div>
        ) : null}
        {description ? (
          <Text variant="body-sm" tone="muted">
            {description}
          </Text>
        ) : null}
        {children}
      </>
    );

    if (as === 'div') {
      const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        onKeyDown?.(event);
        if (event.defaultPrevented || disabled) return;
        // Only when the card itself has focus — a control inside it that
        // receives Space must keep it.
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect?.();
        }
      };
      const divRef = ref as React.Ref<HTMLDivElement>;
      return (
        <div
          ref={divRef}
          role={role ?? 'button'}
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled || undefined}
          className={cn(
            optionCardRecipe({ selected, padding, radius }),
            className
          )}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          {...stateProps}
          {...(props as React.HTMLAttributes<HTMLDivElement>)}
        >
          {content}
        </div>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        role={role}
        disabled={disabled}
        className={cn(
          optionCardRecipe({ selected, padding, radius }),
          className
        )}
        onClick={handleClick}
        onKeyDown={onKeyDown}
        {...stateProps}
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {content}
      </button>
    );
  }
);
